<template>
  <div class="fancy-title" :class="`fancy-title--${size}`">
    <div class="fancy-title__inner">
      <h1 v-if="h1" :class="`c-${theme}`" class="fancy-title__text">
        <slot name="title">
          <icon-text v-if="icon" :icon="icon">
            {{ translateTitleCase(title, titleParams) }}
          </icon-text>
          <clean-wrapper v-else>
            {{ translateTitleCase(title, titleParams) }}
          </clean-wrapper>
        </slot>
      </h1>
      <h2 v-else :class="`c-${theme}`" class="fancy-title__text">
        <slot name="title">
          <icon-text v-if="icon" :icon="icon">
            {{ translateTitleCase(title, titleParams) }}
          </icon-text>
          <clean-wrapper v-else>
            {{ translateTitleCase(title, titleParams) }}
          </clean-wrapper>
        </slot>
      </h2>
      <div
        v-if="showContent && (content || $slots['default'])"
        :class="`c-${theme}`"
        class="fancy-title__sub-title">
        <slot>
          <p :class="`c-${theme}`">
            {{ translateUcFirst(content, contentParams) }}
          </p>
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    title: {
      type: String,
      default: "",
    },
    titleParams: {
      type: Object,
      default: {},
    },
    content: {
      type: [String, Boolean],
      default: false,
    },
    contentParams: {
      type: Object,
      default: {},
    },
    showContent: {
      type: Boolean,
      default: true,
    },
    h1: {
      type: Boolean,
      default: false,
    },
    align: {
      type: [String, Boolean],
      default: false,
    },
    alignment: {
      type: [String, Boolean],
      default: false,
    },
    defaultAlignment: {
      type: String,
      default: "start",
    },
    theme: {
      type: String,
      default: "text",
    },
    separatorTheme: {
      type: String,
      default: "lead",
    },
    icon: {
      type: [String, Boolean],
      default: false,
    },
    size: {
      type: String,
      default: "medium",
    },
  },
  data: function () {
    return {};
  },

  computed: {
    finalAlign() {
      let align = this.defaultAlignment;

      if (this.alignment) {
        align = this.alignment;
      }
      if (this.align) {
        align = this.align;
      }

      if (align.startsWith("inline-")) {
        align = align.replace("inline-", "");
      }

      return align;
    },
    isCentered() {
      return this.finalAlign === "center";
    },
    separatorStart() {
      if (this.finalAlign === "center") {
        return "50%";
      }

      if (this.finalAlign === "start") {
        return 0;
      }

      if (this.finalAlign === "end") {
        return "auto";
      }
    },
    separatorEnd() {
      if (this.finalAlign === "center") {
        return "50%";
      }

      if (this.finalAlign === "start") {
        return "auto";
      }

      if (this.finalAlign === "end") {
        return 0;
      }
    },
    separatorTransform() {
      if (!this.isCentered) {
        return "none";
      }

      if (this.finalAlign === "end") {
        return "translateX(50%)";
      }
      return "translateX(-50%)";
    },
    flexAlignment() {
      if (this.finalAlign === "center") {
        return "center";
      }
      return "flex-" + this.finalAlign;
    },
    textAlign() {
      if (this.finalAlign === "center") {
        return "center";
      }

      if (this.finalAlign === "end") {
        return this.isLanguageRtl() ? "left" : "right";
      }

      return this.isLanguageRtl() ? "right" : "left";
    },
    separatorColor() {
      return `var(--c-${this.separatorTheme})`;
    },
  },
};
</script>

<style scoped lang="scss">
.fancy-title {
  display: flex;
  align-items: center;
  justify-content: v-bind(flexAlignment);
  margin-bottom: var(--margin-max);
  text-align: v-bind(textAlign);

  &__inner {
    display: flex;
    flex-direction: column;
    gap: var(--margin-s);
    align-items: v-bind(flexAlignment);
    justify-content: center;
    text-align: v-bind(textAlign);
  }

  &__text {
    margin-bottom: var(--margin-m);
    position: relative;

    &::after {
      content: " ";
      position: absolute;
      top: calc(100% + var(--margin-m));
      inset-inline-start: v-bind(separatorStart);
      inset-inline-end: v-bind(separatorEnd);
      transform: v-bind(separatorTransform);
      width: clamp(100px, 55%, 500px);
      height: 3px;
      flex: 0 0 3px;
      background-color: v-bind(separatorColor);
    }
  }

  &__sub-title {
    margin-top: var(--margin-s);
    font-size: var(--font-m);
    text-align: v-bind(textAlign);
    max-width: 500px;
  }

  &--small,
  &--s {
    .fancy-title__text {
      font-size: var(--font-2xl);
    }
  }
}
</style>
